import { appRoutePaths } from '../Root/navigation';

export type UsageRouteEvent = {
  action?: string;
  docs_path?: string;
  docs_path_depth?: number;
  entity_kind?: string;
  entity_ref?: string;
  event_type:
    | 'catalog_entity_tab_view'
    | 'catalog_entity_view'
    | 'docs_view'
    | 'page_view'
    | 'search_view';
  route_group: string;
  route_pattern: string;
};

const titleSegment = (value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const entityRef = ({
  kind,
  name,
  namespace,
}: {
  kind?: string;
  name?: string;
  namespace?: string;
}) => {
  if (!kind || !namespace || !name) {
    return undefined;
  }

  return `${kind.toLowerCase()}:${namespace}/${name}`;
};

const staticPages: Record<string, UsageRouteEvent> = {
  [appRoutePaths.access]: {
    event_type: 'page_view',
    route_group: 'access',
    route_pattern: appRoutePaths.access,
  },
  [appRoutePaths.apis]: {
    event_type: 'page_view',
    route_group: 'apis',
    route_pattern: appRoutePaths.apis,
  },
  [appRoutePaths.catalog]: {
    event_type: 'page_view',
    route_group: 'catalog',
    route_pattern: appRoutePaths.catalog,
  },
  [appRoutePaths.catalogGraph]: {
    event_type: 'page_view',
    route_group: 'catalog_graph',
    route_pattern: appRoutePaths.catalogGraph,
  },
  [appRoutePaths.docs]: {
    event_type: 'docs_view',
    route_group: 'docs_index',
    route_pattern: appRoutePaths.docs,
  },
  [appRoutePaths.groups]: {
    event_type: 'page_view',
    route_group: 'groups',
    route_pattern: appRoutePaths.groups,
  },
  [appRoutePaths.home]: {
    event_type: 'page_view',
    route_group: 'home',
    route_pattern: appRoutePaths.home,
  },
  [appRoutePaths.import]: {
    event_type: 'page_view',
    route_group: 'catalog_import',
    route_pattern: appRoutePaths.import,
  },
  [appRoutePaths.search]: {
    event_type: 'search_view',
    route_group: 'search',
    route_pattern: appRoutePaths.search,
  },
  [appRoutePaths.settings]: {
    event_type: 'page_view',
    route_group: 'settings',
    route_pattern: appRoutePaths.settings,
  },
};

export const classifyUsageRoute = (pathname: string): UsageRouteEvent => {
  const cleanPath = pathname.split('?')[0] || '/';
  const staticPage = staticPages[cleanPath];
  if (staticPage) {
    return staticPage;
  }

  const segments = cleanPath.split('/').filter(Boolean).map(titleSegment);

  if (segments[0] === 'docs' && segments.length >= 4) {
    const [, namespace, kind, name, ...docsPath] = segments;
    const cleanDocsPath = docsPath.filter(Boolean);

    return {
      docs_path: cleanDocsPath.join('/'),
      docs_path_depth: cleanDocsPath.length,
      entity_kind: kind,
      entity_ref: entityRef({ kind, name, namespace }),
      event_type: 'docs_view',
      route_group: 'docs_entity',
      route_pattern: '/docs/:namespace/:kind/:name/*',
    };
  }

  if (segments[0] === 'catalog' && segments.length >= 4) {
    const [, namespace, kind, name, tab] = segments;
    const ref = entityRef({ kind, name, namespace });

    if (tab) {
      return {
        action: tab,
        entity_kind: kind,
        entity_ref: ref,
        event_type: 'catalog_entity_tab_view',
        route_group: 'catalog_entity_tab',
        route_pattern: '/catalog/:namespace/:kind/:name/:tab',
      };
    }

    return {
      entity_kind: kind,
      entity_ref: ref,
      event_type: 'catalog_entity_view',
      route_group: 'catalog_entity',
      route_pattern: '/catalog/:namespace/:kind/:name',
    };
  }

  return {
    event_type: 'page_view',
    route_group: 'unknown',
    route_pattern: cleanPath,
  };
};
